import React, { ReactNode, useCallback, useContext, useMemo } from 'react';
import { Role } from '../../../lib';
import { useAuth } from '../AuthContext';
import { Permission, ROLE_PERMISSIONS } from './permission-context-helpers';

interface PermissionContextProviderProps {
    children: ReactNode;
}

const permissionContextObject = React.createContext<any>({
    hasPermissions: (): boolean => false,
    hasUserCurrentRole: (): boolean => false,
    hasCurrentPermissionAndRole: (): boolean => false,
});

interface PermissionContext {
    hasPermissions: (permissions: Permission | Permission[]) => boolean;
    hasUserCurrentRole: (role: Role) => boolean;
    hasCurrentPermissionAndRole: (
        permissions: Permission | Permission[],
        role: Role
    ) => boolean;
}

const usePermission = (): PermissionContext =>
    useContext(permissionContextObject);

const PermissionContextProvider = ({
    children,
}: PermissionContextProviderProps): React.JSX.Element => {
    const { getRoles } = useAuth();

    /**
     *  This function returns whether the current user has the correct permission based on its roles. A user
     *  might contain multiple roles so we need to check whether at least one of the roles has access to the
     *  expected permission.
     *
     * @param permission - A permission to check if the current user has access to.
     */
    const hasPermission = useCallback(
        (permission: Permission): boolean => {
            const userRoles: string[] = getRoles();

            return userRoles.some((role) => {
                // check for potential roles that haven't been mapped yet. This could mean a new role.
                if (!ROLE_PERMISSIONS[role]) return false;

                // if current role is already mapped, check whether it contains expected permission.
                return ROLE_PERMISSIONS[role].includes(permission);
            });
        },
        [getRoles]
    );

    /**
     *  This function returns whether the current user has the correct permission or permissions for a
     *  specific feature. If the permissions is a list we need to make sure at least one of the user roles
     *  has the permission requested.
     *
     * @param permissions - A single or a list of permissions to check if the current user has access to.
     */
    const hasPermissions = useCallback(
        (permissions: Permission | Permission[]): boolean => {
            // If is a single permission check user has access to it.
            if (typeof permissions === 'string') {
                return hasPermission(permissions);
            }

            // If permissions is a list of permissions, then check user has access to at least one of them.
            return permissions.some((permission) => hasPermission(permission));
        },
        [hasPermission]
    );

    const hasUserCurrentRole = (role: Role): boolean => {
        return getRoles().includes(role);
    };

    const hasCurrentPermissionAndRole = (
        permissions: Permission | Permission[],
        role: Role
    ): boolean => {
        return hasUserCurrentRole(role) && hasPermissions(permissions);
    };

    return (
        <permissionContextObject.Provider
            value={useMemo(
                () => ({
                    hasPermissions: hasPermissions,
                    hasUserCurrentRole: hasUserCurrentRole,
                    hasCurrentPermissionAndRole: hasCurrentPermissionAndRole,
                }),
                [hasPermissions]
            )} // eslint-disable-line react-hooks/exhaustive-deps
        >
            {children}
        </permissionContextObject.Provider>
    );
};

export { usePermission, PermissionContextProvider };
